import { styled, keyframes } from "@pigment-css/react";
import { ActionBarPrimitive, MessagePrimitive, MessagePartPrimitive } from "@assistant-ui/react";
import type { TextMessagePartComponent } from "@assistant-ui/react";
import { Copy, Regenerate } from "@acto/web-common/components";
import { useTranslations } from "@acto/web-common/locales";
import { IconButton } from "@acto/taskpane/field-sales-2/components/primitives/IconButton";
import { MarkdownRenderer } from "./markdown/MarkdownRenderer";
import { AssistantRow, MessageBody, UserBubble, UserRow } from "./messageBubbles";

const ActionBar = styled(ActionBarPrimitive.Root)(({ theme }) => ({
  display: "flex",
  gap: theme.fieldSalesTwo.spacing[8],
  marginTop: theme.fieldSalesTwo.spacing[8],
  color: theme.fieldSalesTwo.colors.neutral[700],
}));

const bounce = keyframes`
  0%, 20%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
  35%, 65% { transform: translateY(4px); }
  80%, 100% { transform: translateY(0); }
`;

const BubbleWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.fieldSalesTwo.spacing[8],
  padding: `${theme.fieldSalesTwo.spacing[4]} 0`,
}));

interface DotProps {
  colorIntensity: 0 | 1 | 2;
}

const Dot = styled("div")<DotProps>(({ theme }) => ({
  width: "6px",
  height: "6px",
  borderRadius: "50%",
  display: "inline-block",
  animation: `${bounce} 2s infinite`,
  variants: [
    { props: { colorIntensity: 0 }, style: { backgroundColor: theme.fieldSalesTwo.colors.neutral[500] } },
    { props: { colorIntensity: 1 }, style: { backgroundColor: theme.fieldSalesTwo.colors.neutral[600] } },
    { props: { colorIntensity: 2 }, style: { backgroundColor: theme.fieldSalesTwo.colors.neutral[700] } },
  ],
}));

const bubbleDelays = ["0s", "0.2s", "0.4s"] as const;

const LoadingIndicator = () => (
  <BubbleWrapper>
    {bubbleDelays.map((delay, i) => (
      <Dot key={i} style={{ animationDelay: delay }} colorIntensity={i as 0 | 1 | 2} />
    ))}
  </BubbleWrapper>
);

const UserTextPart: TextMessagePartComponent = ({ text }) => <div>{text}</div>;

const AssistantTextPart: TextMessagePartComponent = ({ text }) => (
  <>
    <MessagePartPrimitive.InProgress>{!text && <LoadingIndicator />}</MessagePartPrimitive.InProgress>
    {text && <MarkdownRenderer content={text} />}
  </>
);

export function Message() {
  const tChat = useTranslations("chat");

  return (
    <>
      <MessagePrimitive.If user>
        <UserRow>
          <UserBubble role="article" aria-label="user message">
            <MessagePrimitive.Parts components={{ Text: UserTextPart }} />
          </UserBubble>
        </UserRow>
      </MessagePrimitive.If>

      <MessagePrimitive.If assistant>
        <AssistantRow role="article" aria-label="assistant message">
          <MessageBody>
            <MessagePrimitive.Parts components={{ Text: AssistantTextPart }} />
          </MessageBody>
          <ActionBar autohide="always">
            <ActionBarPrimitive.Copy asChild>
              <IconButton aria-label={tChat("copy")}>
                <Copy />
              </IconButton>
            </ActionBarPrimitive.Copy>
            <ActionBarPrimitive.Reload asChild>
              <IconButton aria-label={tChat("regenerate")}>
                <Regenerate />
              </IconButton>
            </ActionBarPrimitive.Reload>
          </ActionBar>
        </AssistantRow>
      </MessagePrimitive.If>
    </>
  );
}
